import type { ReactElement } from 'react';

/* A loading placeholder that occupies the space the content will.
 *
 * The point is not that it looks nicer than "Loading…". It is that the row height matches
 * `--lumiq-row-height`, so when the data arrives nothing moves. Layout shift on a dense table is not
 * cosmetic: an analyst who has already started moving toward a row clicks whatever slid into its
 * place, and on the review screen the rows are approve buttons.
 *
 * `aria-hidden` with a sibling live region rather than announcing each bar: a screen reader
 * enumerating twelve grey rectangles is worse than silence. The caller announces "loading" once.
 */

export interface SkeletonRowsProps {
  readonly rows?: number;
  readonly columns?: number;
}

export function SkeletonRows({ rows = 3, columns = 4 }: SkeletonRowsProps): ReactElement {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className="lq-skeleton__row"
          // The only genuinely dynamic value here, so the only one left inline.
          style={{ gridTemplateColumns: `repeat(${String(columns)}, 1fr)` }}
        >
          {Array.from({ length: columns }, (_, columnIndex) => (
            <div
              key={columnIndex}
              className="lq-skeleton__bar"
              // Varying widths so it reads as text rather than as a progress bar. Derived per cell,
              // so it stays inline.
              style={{
                width: `${String(45 + ((rowIndex * 17 + columnIndex * 29) % 45))}%`,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
